// src/pages/Inventory.js

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { 
    Container, 
    Typography, 
    Button, 
    Paper, 
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Box,
    Chip,
    Grid,
    Slide,
    MenuItem,
    InputLabel,
    FormControl,
    Select,
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    TablePagination,
    useTheme,
    Card,
    CardContent,
    Alert,
    CircularProgress,
    Divider,
    Menu,
    InputAdornment,
    Tooltip,
    Avatar,
    Snackbar,
    Tabs,
    Tab,
    ToggleButton,
    ToggleButtonGroup,
    ListItemIcon,
    ListItemText,
    Autocomplete,
    FormHelperText
} from '@mui/material';
import {
    Inventory as PackageIcon,
    Add as AddIcon,
    Remove as RemoveIcon,
    Close as CloseIcon,
    TrendingUp,
    TrendingDown,
    FilterList as FilterIcon,
    Search,
    AccountCircle,
    GetApp,
    Print,
    Refresh as RefreshIcon,
    Today as TodayIcon,
    X,
    Category,
    PictureAsPdf,
    TableChart,
    Warning,
    AttachMoney,
    Store,
    ShoppingCart,
    LocalShipping,
    CalendarMonth,
    ViewModule,
    ViewList,
    Edit as EditIcon,
    Visibility as ViewIcon,
    History,
    Assessment,
    Description,
    MoreVert,
    Scale
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import useCustomToast from '../../hooks/useToast';
import {
    APIProductsList,
    APIInventoryUpdate,
    APIInventoryHistory,
    APIUserList,
    APICategoriesNames,
    APIInventoryStats,
    APIInventoryExportExcel,
    APIInventoryExportPDF,
    APIInventoryPrint,
    APIInventoryPrintHistory,
    APIUnitName
} from '../../apiServices/APIs';
import { formatCreatedAtDate, formatNumberWithComma, recordsCount, recordsPerPage, getUserDetails, getCategoriesNameById } from '../../utils/helpers';
import { apiService } from '../../apiServices/ApiService';
import { usePermission } from '../../hooks/usePermission';
import LoadingState from '../../components/LoadingState';
import ErrorState from '../../components/ErrorrState';

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const Inventory = () => {
    const theme = useTheme();
    const [loading, setLoading] = useState(false);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [statsLoading, setStatsLoading] = useState(false);
    const [error, setError] = useState(null);
    const showToast = useCustomToast();
    const [updateId, setUpdateId] = useState('');
    const [postStatus, setPostStatus] = useState('ADD');
    const [exportAnchorEl, setExportAnchorEl] = useState(null);
    const [actionMenuAnchorEl, setActionMenuAnchorEl] = useState(null);
    const [selectedProductForAction, setSelectedProductForAction] = useState(null);
    const [pageState, setPageState] = useState({
        productsData: [],
        inventoryHistory: [],
        usersData: [],
        categoriesData: [],
        unitsData: [],
        inventoryStats: {
            total_products: 0,
            total_stock_value: 0,
            low_stock_items: 0,
            out_of_stock_items: 0,
            total_profit_potential: 0,
            average_stock_level: 0,
            top_performing_categories: [],
            total_inventory_value: 0,
            items_needing_restock: 0
        }
    });
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(recordsPerPage);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [viewMode, setViewMode] = useState('list');
    
    // Search input states for autocomplete
    const [productSearchInput, setProductSearchInput] = useState('');
    
    const [filters, setFilters] = useState({
        name: '',
        category: '',
        unit_id: '',
        minStock: '',
        maxStock: '',
        minCost: '',
        maxCost: '',
        minSelling: '',
        maxSelling: '',
        dateFrom: '',
        dateTo: ''
    });
    
    const [inventoryForm, setInventoryForm] = useState({
        actionType: 'restock',
        productId: '',
        quantity: '',
        reason: ''
    });

    const [openAdjustment, setOpenAdjustment] = useState(false);
    const [openHistory, setOpenHistory] = useState(false);
    const [openInventoryModal, setOpenInventoryModal] = useState(false);
    const [filterOptions, setFilterOptions] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

    const { hasPermission, loading: permissionLoading } = usePermission();

    useEffect(() => {
        apiService.setToastFunction(showToast);
        fetchData();
        fetchInventoryStats();
    }, [showToast]);

    const fetchData = async () => {
        setLoading(true);
        setError(null);

        try {
            const userBranchId = getUserDetails("branchId");

            const [productsResult, usersResult, categoriesResult, unitsResult] = await Promise.all([
                apiService.list(userBranchId, APIProductsList),
                apiService.list(userBranchId, APIUserList),
                apiService.list(userBranchId, APICategoriesNames),
                apiService.list(userBranchId, APIUnitName)
            ]);

            if (productsResult.success && usersResult.success && categoriesResult.success) {
                const products = Array.isArray(productsResult.data) ? productsResult.data : [];
                const users = Array.isArray(usersResult.data) ? usersResult.data : [];
                const categories = Array.isArray(categoriesResult.data) ? 
                    categoriesResult.data.filter(cat => cat.status === 1) : [];
                const units = Array.isArray(unitsResult.data) ? unitsResult.data : [];

                setPageState(prev => ({
                    ...prev,
                    productsData: products,
                    usersData: users,
                    categoriesData: categories,
                    unitsData: units
                }));
            } else {
                setError(productsResult.message || usersResult.message || categoriesResult.message);
            }
        } catch (error) {
            setError("Failed to load data");
            console.error("Fetch error:", error);
        } finally {
            setLoading(false);
        }
    };

    const fetchInventoryStats = async () => {
        setStatsLoading(true);
        try {
            const userBranchId = getUserDetails("branchId");
            const result = await apiService.listStats(userBranchId, APIInventoryStats);
            
            if (result.success && result.data) {
                setPageState(prev => ({
                    ...prev,
                    inventoryStats: result.data
                }));
            } else {
                calculateLocalStats();
            }
        } catch (error) {
            console.error("Error fetching inventory stats:", error);
            calculateLocalStats();
        } finally {
            setStatsLoading(false);
        }
    };

    const calculateLocalStats = () => {
        const products = pageState.productsData;
        
        const totalProducts = products.length;
        const totalStockValue = products.reduce((sum, product) => sum + (product.stock * product.cost), 0);
        const lowStockItems = products.filter(p => p.stock > 0 && p.stock <= 10).length;
        const outOfStockItems = products.filter(p => p.stock === 0).length;
        const itemsNeedingRestock = lowStockItems + outOfStockItems;
        const totalProfitPotential = products.reduce((sum, product) => 
            sum + (product.stock * (product.selling - product.cost)), 0);
        const averageStockLevel = products.length > 0 
            ? Math.round(products.reduce((sum, product) => sum + product.stock, 0) / products.length)
            : 0;
        const totalInventoryValue = products.reduce((sum, product) => 
            sum + (product.stock * product.selling), 0);

        setPageState(prev => ({
            ...prev,
            inventoryStats: {
                ...prev.inventoryStats,
                total_products: totalProducts,
                total_stock_value: totalStockValue,
                low_stock_items: lowStockItems,
                out_of_stock_items: outOfStockItems,
                items_needing_restock: itemsNeedingRestock,
                total_profit_potential: totalProfitPotential,
                total_inventory_value: totalInventoryValue,
                average_stock_level: averageStockLevel
            }
        }));
    };

    useEffect(() => {
        if (pageState.productsData.length > 0 && !statsLoading) {
            calculateLocalStats();
        }
    }, [pageState.productsData]);

    const handleExportClick = (event) => { setExportAnchorEl(event.currentTarget); };
    const handleExportClose = () => { setExportAnchorEl(null); };

    const downloadPdfFile = async (url, formData, defaultFilename) => {
        try {
            const response = await fetch(url, {
                method: 'POST',
                body: formData,
                headers: {
                    'Accept': 'application/pdf, application/json'
                }
            });

            if (!response.ok) {
                const errorText = await response.text();
                let errorMessage = "Failed to generate PDF document";
                try {
                    const errorJson = JSON.parse(errorText);
                    errorMessage = errorJson.message || errorMessage;
                } catch (e) {}
                throw new Error(errorMessage);
            }

            const blob = await response.blob();
            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.setAttribute('download', defaultFilename);
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            window.URL.revokeObjectURL(downloadUrl);
            return { success: true };
        } catch (error) {
            console.error("PDF download helper error:", error);
            return { success: false, message: error.message };
        }
    };

    const handlePrint = async (record) => {
        if (!hasPermission('PRINT')) {
            showToast("Permission Denied", "You don't have permission to print inventory details", "error");
            return;
        }

        try {
            showToast("Processing", "Generating PDF file...", "info");
            const formDataToSend = new FormData();
            formDataToSend.append('print_id', record.id);
            formDataToSend.append('user_name', getUserDetails("userName"));
            formDataToSend.append('branch_id', getUserDetails("branchId"));
            formDataToSend.append('user_id', getUserDetails("Id"));
            formDataToSend.append('print_type', 'product'); 
            
            const filename = `product_${record.name.replace(/\s+/g, '_')}.pdf`;
            const result = await downloadPdfFile(APIInventoryPrint, formDataToSend, filename);

            if (result.success) {
                showToast("Success", "PDF file downloaded successfully", "success");
            } else {
                showToast("Error", result.message || "Failed to generate PDF file", "error");
            }
        } catch (error) {
            console.error("Print error:", error);
            showToast("Error", "Failed to generate PDF file", "error");
        }
    };

    const handleExportExcel = async () => {
        if (!hasPermission('EXPORT')) {
            showToast("Permission Denied", "You don't have permission to export inventory data", "error");
            handleExportClose();
            return;
        }

        try {
            handleExportClose();
            showToast("Processing", "Preparing Excel export...", "info");
            const formDataToSend = new FormData();
            formDataToSend.append('branch_id', getUserDetails("branchId"));
            formDataToSend.append('user_id', getUserDetails("Id"));
            
            if (filters.name) formDataToSend.append('name', filters.name);
            if (filters.category) formDataToSend.append('category', filters.category);
            if (filters.unit_id) formDataToSend.append('unit_id', filters.unit_id);
            if (filters.dateFrom) formDataToSend.append('date_from', filters.dateFrom);
            if (filters.dateTo) formDataToSend.append('date_to', filters.dateTo);
            
            const result = await apiService.exportToExcel(APIInventoryExportExcel, formDataToSend);
            if (result && result.success) {
                showToast("Success", "Excel file downloaded successfully", "success");
            } else {
                showToast("Error", result.message || "Failed to export Excel file", "error");
            }
        } catch (error) {
            console.error('Excel export error:', error);
            showToast("Error", error.message || "Failed to export Excel file", "error");
        }
    };

    const handleExportPDF = async () => {
        if (!hasPermission('EXPORT')) {
            showToast("Permission Denied", "You don't have permission to export inventory data", "error");
            handleExportClose();
            return;
        }

        try {
            handleExportClose();
            showToast("Processing", "Preparing PDF export...", "info");

            const formDataToSend = new FormData();
            formDataToSend.append('branch_id', getUserDetails("branchId"));
            formDataToSend.append('user_name', getUserDetails("userName"));
            formDataToSend.append('user_id', getUserDetails("Id"));
            
            if (filters.name) formDataToSend.append('name', filters.name);
            if (filters.category) formDataToSend.append('category', filters.category);
            if (filters.unit_id) formDataToSend.append('unit_id', filters.unit_id);
            
            const result = await apiService.exportToPDF(APIInventoryExportPDF, formDataToSend);

            if (result && result.success) {
                showToast("Success", "PDF file downloaded successfully", "success");
            } else {
                showToast("Error", result.message || "Failed to export PDF file", "error");
            }
        } catch (error) {
            console.error('PDF export error:', error);
            showToast("Error", error.message || "Failed to export PDF file", "error");
        }
    };

    const handlePrintHistory = async (product) => {
        if (!hasPermission('PRINT')) {
            showToast("Permission Denied", "You don't have permission to print inventory history", "error");
            return;
        }

        try {
            showToast("Processing", "Generating inventory history PDF...", "info");
            const formDataToSend = new FormData();
            formDataToSend.append('product_id', product.id);
            formDataToSend.append('user_name', getUserDetails("userName"));
            formDataToSend.append('branch_id', getUserDetails("branchId"));
            formDataToSend.append('user_id', getUserDetails("Id"));
            
            const filename = `inventory_history_${product.name.replace(/\s+/g, '_')}.pdf`;
            const result = await downloadPdfFile(APIInventoryPrintHistory, formDataToSend, filename);

            if (result.success) {
                showToast("Success", "Inventory history PDF generated successfully", "success");
            } else {
                showToast("Error", result.message || "Failed to generate PDF file", "error");
            }
        } catch (error) {
            console.error("Print history error:", error);
            showToast("Error", "Failed to generate PDF file", "error");
        }
    };

    // Get unit name for a product
    const getUnitName = (product) => {
        if (product && product.unit_id) {
            const unit = pageState.unitsData.find(u => u.id === product.unit_id);
            return unit ? `${unit.name} (${unit.code})` : 'N/A';
        }
        return 'N/A';
    };

    // Get unit code for a product
    const getUnitCode = (product) => {
        if (product && product.unit_id) {
            const unit = pageState.unitsData.find(u => u.id === product.unit_id);
            return unit ? unit.code : 'N/A';
        }
        return 'N/A';
    };

    const filteredData = pageState.productsData.filter(product => {
        const unitName = getUnitName(product);
        
        const matchesSearch = searchTerm === '' ||
            product.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (product.category && product.category.toString().includes(searchTerm)) ||
            unitName.toLowerCase().includes(searchTerm.toLowerCase());

        const matchesName = !filters.name ||
            product.name?.toLowerCase().includes(filters.name.toLowerCase());

        const matchesCategory = !filters.category ||
            String(product.category) === String(filters.category);

        const matchesUnit = !filters.unit_id ||
            String(product.unit_id) === String(filters.unit_id);

        let matchesStock = true;
        if (filters.minStock) {
            matchesStock = matchesStock && (product.stock >= parseInt(filters.minStock));
        }
        if (filters.maxStock) {
            matchesStock = matchesStock && (product.stock <= parseInt(filters.maxStock));
        }

        let matchesCost = true;
        if (filters.minCost) {
            matchesCost = matchesCost && (product.cost >= parseFloat(filters.minCost));
        }
        if (filters.maxCost) {
            matchesCost = matchesCost && (product.cost <= parseFloat(filters.maxCost));
        }

        let matchesSelling = true;
        if (filters.minSelling) {
            matchesSelling = matchesSelling && (product.selling >= parseFloat(filters.minSelling));
        }
        if (filters.maxSelling) {
            matchesSelling = matchesSelling && (product.selling <= parseFloat(filters.maxSelling));
        }

        let matchesDate = true;
        if (filters.dateFrom) {
            const productDate = new Date(product.updated_at).toISOString().split('T')[0];
            const filterFromDate = new Date(filters.dateFrom).toISOString().split('T')[0];
            matchesDate = matchesDate && productDate >= filterFromDate;
        }
        if (filters.dateTo) {
            const productDate = new Date(product.updated_at).toISOString().split('T')[0];
            const filterToDate = new Date(filters.dateTo).toISOString().split('T')[0];
            matchesDate = matchesDate && productDate <= filterToDate;
        }

        return matchesSearch && matchesName && matchesCategory && matchesUnit &&
               matchesStock && matchesCost && matchesSelling && matchesDate;
    });

    // Calculate filtered totals
    const calculateFilteredTotals = () => {
        const totalStock = filteredData.reduce((sum, product) => sum + (parseInt(product.stock) || 0), 0);
        const totalCostValue = filteredData.reduce((sum, product) => sum + (parseInt(product.stock) * parseFloat(product.cost) || 0), 0);
        const totalSellingValue = filteredData.reduce((sum, product) => sum + (parseInt(product.stock) * parseFloat(product.selling) || 0), 0);
        const totalProfitPotential = filteredData.reduce((sum, product) => sum + (parseInt(product.stock) * (parseFloat(product.selling) - parseFloat(product.cost)) || 0), 0);
        const averageSellingPrice = filteredData.length > 0 ? filteredData.reduce((sum, p) => sum + parseFloat(p.selling), 0) / filteredData.length : 0;
        const averageCostPrice = filteredData.length > 0 ? filteredData.reduce((sum, p) => sum + parseFloat(p.cost), 0) / filteredData.length : 0;
        
        const inStockItems = filteredData.filter(p => p.stock > 0).length;
        const outOfStockItems = filteredData.filter(p => p.stock === 0).length;
        const lowStockItems = filteredData.filter(p => p.stock > 0 && p.stock <= 10).length;

        return {
            totalStock,
            totalCostValue,
            totalSellingValue,
            totalProfitPotential,
            averageSellingPrice,
            averageCostPrice,
            inStockItems,
            outOfStockItems,
            lowStockItems,
            totalCount: filteredData.length
        };
    };

    const filteredTotals = calculateFilteredTotals();

    const calculateProfit = (product) => {
        return (product.selling || 0) - (product.cost || 0);
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-UG', {
            style: 'currency',
            currency: 'UGX',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(amount || 0);
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleFilterSelectChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleFilterOptions = () => setFilterOptions(true);
    const handleCloseOptions = () => setFilterOptions(false);

    const applyTodayFilter = () => {
        const today = new Date().toISOString().split('T')[0];
        setFilters({
            ...filters,
            dateFrom: today,
            dateTo: today
        });
        handleCloseOptions();
    };

    const applyThisMonthFilter = () => {
        const today = new Date();
        const firstDay = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0];
        const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0).toISOString().split('T')[0];

        setFilters({
            ...filters,
            dateFrom: firstDay,
            dateTo: lastDay
        });
        handleCloseOptions();
    };

    const clearFilters = () => {
        setFilters({
            name: '',
            category: '',
            unit_id: '',
            minStock: '',
            maxStock: '',
            minCost: '',
            maxCost: '',
            minSelling: '',
            maxSelling: '',
            dateFrom: '',
            dateTo: ''
        });
        setSearchTerm('');
        handleCloseOptions();
    };

    const applyFilters = () => {
        handleCloseOptions();
    };

    const handleInventoryInputChange = (e) => {
        const { name, value } = e.target;
        setInventoryForm(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Handler for product selection in autocomplete
    const handleProductSelect = (event, selectedProduct) => {
        setInventoryForm(prev => ({
            ...prev,
            productId: selectedProduct ? selectedProduct.id : ''
        }));
        if (selectedProduct) {
            setSelectedProduct(selectedProduct);
        }
    };

    const handleOpenAdjustment = (product = null) => {
        if (!hasPermission('EDIT')) {
            showToast("Permission Denied", "You don't have permission to adjust stock", "error");
            return;
        }

        if (product) {
            setSelectedProduct(product);
            setProductSearchInput(product.name);
            setInventoryForm({
                actionType: 'restock',
                productId: product.id,
                quantity: '',
                reason: ''
            });
        } else {
            setSelectedProduct(null);
            setProductSearchInput('');
            setInventoryForm({
                actionType: 'restock',
                productId: '',
                quantity: '',
                reason: ''
            });
        }
        setOpenAdjustment(true);
        handleActionMenuClose();
    };

    const handleCloseAdjustment = () => {
        setOpenAdjustment(false);
        setSelectedProduct(null);
        setProductSearchInput('');
        setInventoryForm({
            actionType: 'restock',
            productId: '',
            quantity: '',
            reason: ''
        });
    };

    const handleAdjustStock = async (e) => {
        e.preventDefault();

        if (!hasPermission('EDIT')) {
            showToast("Permission Denied", "You don't have permission to adjust stock", "error");
            return;
        }

        if (!inventoryForm.productId) {
            showToast("Validation Error", "Please select a product", "warning");
            return;
        }
        if (!inventoryForm.quantity || parseInt(inventoryForm.quantity) <= 0) {
            showToast("Validation Error", "Please enter a valid quantity", "warning");
            return;
        }

        if (inventoryForm.actionType === 'adjustment') {
            const selectedProd = pageState.productsData.find(p => p.id == inventoryForm.productId);
            if (selectedProd && parseInt(inventoryForm.quantity) > selectedProd.stock) {
                showToast("Validation Error", `Cannot remove more than current stock (${selectedProd.stock} pcs)`, "warning");
                return;
            }
        }

        const formDataToSend = new FormData();
        formDataToSend.append('user_id', getUserDetails("Id"));
        formDataToSend.append('branch_id', getUserDetails("branchId"));
        formDataToSend.append('product_id', inventoryForm.productId);
        formDataToSend.append('quantity', inventoryForm.quantity);
        formDataToSend.append('user', getUserDetails("Id")); 
        formDataToSend.append('action_type', inventoryForm.actionType);
        formDataToSend.append('reason', inventoryForm.reason || 'Inventory adjustment');

        try {
            const result = await apiService.create(APIInventoryUpdate, formDataToSend);

            if (result && result.success) {
                showToast("Success", result.message, "success");
                handleCloseAdjustment();
                handleCloseInventoryModal();
                fetchData();
                fetchInventoryStats();
            }
        } catch (error) {
            console.error("API Error:", error);
            showToast("Error", error.message || "Failed to update inventory", "error");
        }
    };

    const handleOpenInventoryModal = (product = null) => {
        if (!hasPermission('EDIT')) {
            showToast("Permission Denied", "You don't have permission to manage inventory", "error");
            return;
        }

        if (product) {
            setSelectedProduct(product);
            setProductSearchInput(product.name);
            setInventoryForm({
                actionType: 'restock',
                productId: product.id,
                quantity: '',
                reason: ''
            });
        } else {
            setSelectedProduct(null);
            setProductSearchInput('');
            setInventoryForm({
                actionType: 'restock',
                productId: '',
                quantity: '',
                reason: ''
            });
        }
        setOpenInventoryModal(true);
    };

    const handleCloseInventoryModal = () => {
        setOpenInventoryModal(false);
        setSelectedProduct(null);
        setProductSearchInput('');
        setInventoryForm({
            actionType: 'restock',
            productId: '',
            quantity: '',
            reason: ''
        });
    };

    const handleOpenHistory = async (product) => {
        setSelectedProduct(product);
        setOpenHistory(true);
        await fetchInventoryHistory(product.id);
        handleActionMenuClose();
    };

    const handleCloseHistory = () => {
        setOpenHistory(false);
        setSelectedProduct(null);
        setPageState(prev => ({
            ...prev,
            inventoryHistory: []
        }));
    };

    const fetchInventoryHistory = async (productId) => {
        console.log("📡 Fetching history for product ID:", productId);
        setHistoryLoading(true);
        
        try {
            const userBranchId = getUserDetails("branchId");
            const result = await apiService.listHistory(userBranchId, APIInventoryHistory, productId);
            
            console.log("📥 API Response:", result);
            
            if (result.success && result.data) {
                let historyData = [];
                
                if (Array.isArray(result.data.history)) {
                    historyData = result.data.history;
                } else if (Array.isArray(result.data)) {
                    historyData = result.data;
                }
                
                console.log("✅ Processed history data:", historyData);
                
                setPageState(prev => ({
                    ...prev,
                    inventoryHistory: historyData
                }));
                
                if (historyData.length === 0) {
                    showToast("Info", "No history records found for this product", "info");
                }
            } else {
                console.warn("⚠️ Failed to fetch history:", result);
                setPageState(prev => ({
                    ...prev,
                    inventoryHistory: []
                }));
                showToast("Info", "No history records found", "info");
            }
        } catch (error) {
            console.error("❌ Error fetching history:", error);
            setPageState(prev => ({
                ...prev,
                inventoryHistory: []
            }));
            showToast("Error", "Failed to fetch inventory history", "error");
        } finally {
            setHistoryLoading(false);
        }
    };

    // Action menu handlers
    const handleActionMenuOpen = (event, product) => {
        event.stopPropagation();
        setActionMenuAnchorEl(event.currentTarget);
        setSelectedProductForAction(product);
    };

    const handleActionMenuClose = () => {
        setActionMenuAnchorEl(null);
        setSelectedProductForAction(null);
    };

    const formatActionType = (action) => {
        const actions = {
            'restock': { label: 'Restock', color: 'success', icon: <TrendingUp /> },
            'adjustment': { label: 'Adjustment', color: 'error', icon: <TrendingDown /> },
            'sale': { label: 'Sale', color: 'warning', icon: <RemoveIcon /> },
            'return': { label: 'Return', color: 'info', icon: <AddIcon /> }
        };
        return actions[action] || { label: action, color: 'default', icon: null };
    };

    if (permissionLoading) {
        return <LoadingState message="Loading permissions..." variant="medical" size="large" fullPage />;
    }

    if (loading) {
        return (
            <LoadingState
                message="Loading Inventory Data..."
                variant="medical"
                size="large"
                fullPage
            />
        );
    }

    if (error) {
        return (
            <ErrorState
                error={error}
                onRetry={fetchData}
                title="Unable to Load Inventory Data"
                variant="medical"
                fullPage
                retryText="Retry Loading"
            />
        );
    }

    return (
        <Container maxWidth="xl" sx={{ py: 3 }}>
            {/* Inventory Statistics Cards */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} sm={6} md={3}>
                    <Paper elevation={2} sx={{ p: 2, borderRadius: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Box>
                                <Typography variant="body2" color="text.secondary">Total Products</Typography>
                                <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
                                    {statsLoading ? <CircularProgress size={24} /> : 
                                        formatNumberWithComma(pageState.inventoryStats.total_products || 0)}
                                </Typography>
                            </Box>
                            <Avatar sx={{ bgcolor: theme.palette.primary.light, width: 48, height: 48 }}>
                                <PackageIcon sx={{ fontSize: 24 }} />
                            </Avatar>
                        </Box>
                    </Paper>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <Paper elevation={2} sx={{ p: 2, borderRadius: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Box>
                                <Typography variant="body2" color="text.secondary">Stock Value</Typography>
                                <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.success.main }}>
                                    {statsLoading ? <CircularProgress size={24} /> : 
                                        formatCurrency(pageState.inventoryStats.total_stock_value || 0)}
                                </Typography>
                            </Box>
                            <Avatar sx={{ bgcolor: theme.palette.success.light, width: 48, height: 48 }}>
                                <AttachMoney sx={{ fontSize: 24 }} />
                            </Avatar>
                        </Box>
                    </Paper>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <Paper elevation={2} sx={{ p: 2, borderRadius: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Box>
                                <Typography variant="body2" color="text.secondary">Inventory Value</Typography>
                                <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.warning.main }}>
                                    {statsLoading ? <CircularProgress size={24} /> : 
                                        formatCurrency(pageState.inventoryStats.total_inventory_value || 0)}
                                </Typography>
                            </Box>
                            <Avatar sx={{ bgcolor: theme.palette.warning.light, width: 48, height: 48 }}>
                                <Store sx={{ fontSize: 24 }} />
                            </Avatar>
                        </Box>
                    </Paper>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <Paper elevation={2} sx={{ p: 2, borderRadius: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Box>
                                <Typography variant="body2" color="text.secondary">Low Stock</Typography>
                                <Typography variant="h6" sx={{ fontWeight: 'bold', color: (pageState.inventoryStats.low_stock_items || 0) > 0 ? theme.palette.warning.main : theme.palette.success.main }}>
                                    {statsLoading ? <CircularProgress size={24} /> : 
                                        formatNumberWithComma(pageState.inventoryStats.low_stock_items || 0)}
                                </Typography>
                            </Box>
                            <Avatar sx={{ bgcolor: theme.palette.warning.light, width: 48, height: 48 }}>
                                <Warning sx={{ fontSize: 24 }} />
                            </Avatar>
                        </Box>
                    </Paper>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <Paper elevation={2} sx={{ p: 2, borderRadius: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Box>
                                <Typography variant="body2" color="text.secondary">Out of Stock</Typography>
                                <Typography variant="h6" sx={{ fontWeight: 'bold', color: (pageState.inventoryStats.out_of_stock_items || 0) > 0 ? theme.palette.error.main : theme.palette.success.main }}>
                                    {statsLoading ? <CircularProgress size={24} /> : 
                                        formatNumberWithComma(pageState.inventoryStats.out_of_stock_items || 0)}
                                </Typography>
                            </Box>
                            <Avatar sx={{ bgcolor: theme.palette.error.light, width: 48, height: 48 }}>
                                <ShoppingCart sx={{ fontSize: 24 }} />
                            </Avatar>
                        </Box>
                    </Paper>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <Paper elevation={2} sx={{ p: 2, borderRadius: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Box>
                                <Typography variant="body2" color="text.secondary">Need Restock</Typography>
                                <Typography variant="h6" sx={{ fontWeight: 'bold', color: (pageState.inventoryStats.items_needing_restock || 0) > 0 ? theme.palette.error.main : theme.palette.success.main }}>
                                    {statsLoading ? <CircularProgress size={24} /> : 
                                        formatNumberWithComma(pageState.inventoryStats.items_needing_restock || 0)}
                                </Typography>
                            </Box>
                            <Avatar sx={{ bgcolor: theme.palette.error.light, width: 48, height: 48 }}>
                                <LocalShipping sx={{ fontSize: 24 }} />
                            </Avatar>
                        </Box>
                    </Paper>
                </Grid>
            </Grid>

            {/* Search and Filter Bar */}
            <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    <Typography variant="h6" sx={{
                        fontWeight: 'bold',
                        color: theme.palette.primary.main,
                        flexShrink: 0
                    }}>
                        Inventory Management ({recordsCount(pageState.productsData.length)} Total)
                    </Typography>

                    <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
                        {hasPermission('EDIT') && (
                            <Button
                                variant="contained"
                                color="warning"
                                startIcon={<AddIcon />}
                                onClick={() => handleOpenInventoryModal()}
                                sx={{ textTransform: 'none', borderRadius: '8px' }}
                                size="small"
                            >
                                Manage Inventory
                            </Button>
                        )}

                        {hasPermission('VIEW') && (
                            <Button
                                variant="contained"
                                color="secondary"
                                startIcon={<FilterIcon />}
                                onClick={handleFilterOptions}
                                sx={{ textTransform: 'none', borderRadius: '8px' }}
                                size="small"
                            >
                                View Options
                            </Button>
                        )}

                        {hasPermission('EXPORT') && (
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<GetApp />}
                                onClick={handleExportClick}
                                sx={{ textTransform: 'none', borderRadius: '8px' }}
                                size="small"
                            >
                                Export Data
                            </Button>
                        )}

                        <Button
                            variant="outlined"
                            color="info"
                            startIcon={<RefreshIcon />}
                            onClick={() => {
                                fetchData();
                                fetchInventoryStats();
                            }}
                            sx={{ textTransform: 'none', borderRadius: '8px' }}
                            size="small"
                        >
                            Refresh
                        </Button>

                        <ToggleButtonGroup
                            value={viewMode}
                            exclusive
                            onChange={(e, val) => val && setViewMode(val)}
                            size="small"
                        >
                            <ToggleButton value="list">
                                <ViewList />
                            </ToggleButton>
                            <ToggleButton value="grid">
                                <ViewModule />
                            </ToggleButton>
                        </ToggleButtonGroup>
                    </Box>

                    <Paper sx={{
                        width: '300px',
                        p: '2px 8px',
                        display: 'flex',
                        alignItems: 'center',
                        marginLeft: 'auto',
                        borderRadius: '8px',
                        background: theme.palette.primary.light
                    }}>
                        <TextField
                            fullWidth
                            variant="outlined"
                            placeholder="Search products..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            InputProps={{
                                startAdornment: <Search sx={{ mr: 1, color: 'white' }} />,
                                sx: {
                                    '& fieldset': { border: 'none' },
                                    fontSize: '0.875rem',
                                    color: 'white',
                                    '& input': {
                                        color: 'white',
                                        '&::placeholder': {
                                            color: 'rgba(255, 255, 255, 0.7)',
                                            opacity: 1
                                        }
                                    }
                                }
                            }}
                            size="small"
                        />
                    </Paper>
                </Box>

                {/* Active Filters */}
                {(filters.name || filters.category || filters.unit_id || filters.minStock || filters.maxStock || 
                  filters.minCost || filters.maxCost || filters.minSelling || filters.maxSelling || 
                  filters.dateFrom || filters.dateTo) && (
                    <Box sx={{
                        display: 'flex',
                        gap: 1,
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        mt: 2,
                        pt: 2,
                        borderTop: '1px solid',
                        borderColor: 'divider'
                    }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                            Active filters:
                        </Typography>
                        {filters.name && (
                            <Chip
                                label={`Name: ${filters.name}`}
                                onDelete={() => setFilters(prev => ({ ...prev, name: '' }))}
                                color="primary"
                                variant="outlined"
                                size="small"
                            />
                        )}
                        {filters.category && (
                            <Chip
                                label={`Category: ${getCategoriesNameById(filters.category, pageState.categoriesData)}`}
                                onDelete={() => setFilters(prev => ({ ...prev, category: '' }))}
                                color="primary"
                                variant="outlined"
                                size="small"
                            />
                        )}
                        {filters.unit_id && (
                            <Chip
                                label={`Unit: ${pageState.unitsData.find(u => u.id === parseInt(filters.unit_id))?.name || 'N/A'}`}
                                onDelete={() => setFilters(prev => ({ ...prev, unit_id: '' }))}
                                color="primary"
                                variant="outlined"
                                size="small"
                            />
                        )}
                        {filters.minStock && (
                            <Chip
                                label={`Min Stock: ${filters.minStock}`}
                                onDelete={() => setFilters(prev => ({ ...prev, minStock: '' }))}
                                color="info"
                                variant="outlined"
                                size="small"
                            />
                        )}
                        {filters.maxStock && (
                            <Chip
                                label={`Max Stock: ${filters.maxStock}`}
                                onDelete={() => setFilters(prev => ({ ...prev, maxStock: '' }))}
                                color="info"
                                variant="outlined"
                                size="small"
                            />
                        )}
                        {filters.dateFrom && (
                            <Chip
                                label={`From: ${new Date(filters.dateFrom).toLocaleDateString()}`}
                                onDelete={() => setFilters(prev => ({ ...prev, dateFrom: '' }))}
                                color="secondary"
                                variant="outlined"
                                size="small"
                            />
                        )}
                        {filters.dateTo && (
                            <Chip
                                label={`To: ${new Date(filters.dateTo).toLocaleDateString()}`}
                                onDelete={() => setFilters(prev => ({ ...prev, dateTo: '' }))}
                                color="secondary"
                                variant="outlined"
                                size="small"
                            />
                        )}
                        <Button
                            size="small"
                            onClick={clearFilters}
                            startIcon={<CloseIcon fontSize="small" />}
                            sx={{ ml: 1 }}
                        >
                            Clear All
                        </Button>
                    </Box>
                )}
            </Paper>

            {/* Export Menu */}
            <Menu
                anchorEl={exportAnchorEl}
                open={Boolean(exportAnchorEl)}
                onClose={handleExportClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
            >
                <MenuItem onClick={handleExportExcel} sx={{ gap: 1 }}>
                    <TableChart fontSize="small" color="success" />
                    Export to Excel (.xlsx)
                </MenuItem>
                <MenuItem onClick={handleExportPDF} sx={{ gap: 1 }}>
                    <PictureAsPdf fontSize="small" color="error" />
                    Export to PDF (.pdf)
                </MenuItem>
            </Menu>

            {/* Action Menu for List Items */}
            <Menu
                anchorEl={actionMenuAnchorEl}
                open={Boolean(actionMenuAnchorEl)}
                onClose={handleActionMenuClose}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
                {hasPermission('EDIT') && (
                    <MenuItem onClick={() => selectedProductForAction && handleOpenAdjustment(selectedProductForAction)}>
                        <ListItemIcon><EditIcon fontSize="small" color="secondary" /></ListItemIcon>
                        <ListItemText sx={{ color: 'secondary.main' }}>Adjust Stock</ListItemText>
                    </MenuItem>
                )}
                {hasPermission('PRINT') && (
                    <MenuItem onClick={() => selectedProductForAction && handlePrint(selectedProductForAction)}>
                        <ListItemIcon><Print fontSize="small" color="warning" /></ListItemIcon>
                        <ListItemText sx={{ color: 'warning.main' }}>Print</ListItemText>
                    </MenuItem>
                )}
                <MenuItem onClick={() => selectedProductForAction && handleOpenHistory(selectedProductForAction)}>
                    <ListItemIcon><History fontSize="small" color="info" /></ListItemIcon>
                    <ListItemText sx={{ color: 'info.main' }}>View History</ListItemText>
                </MenuItem>
            </Menu>

            {/* Products Table */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
            >
                <Paper elevation={2} sx={{ borderRadius: 3, overflow: 'hidden' }}>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow sx={{
                                    backgroundColor: theme.palette.primary.main,
                                    '& th': {
                                        color: 'white',
                                        fontWeight: 'bold',
                                        fontSize: '0.95rem'
                                    }
                                }}>
                                    <TableCell>#</TableCell>
                                    <TableCell>Product</TableCell>
                                    <TableCell>Category</TableCell>
                                    <TableCell>Unit</TableCell>
                                    <TableCell align="center">Current Stock</TableCell>
                                    <TableCell align="center">Cost Price</TableCell>
                                    <TableCell align="center">Selling Price</TableCell>
                                    <TableCell align="center">Profit</TableCell>
                                    <TableCell align="center">Last Updated</TableCell>
                                    <TableCell align="center">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((product, index) => {
                                    const unitName = getUnitName(product);
                                    return (
                                        <TableRow
                                            key={product.id}
                                            hover
                                            sx={{
                                                '&:nth-of-type(odd)': {
                                                    backgroundColor: theme.palette.action.hover
                                                }
                                            }}
                                        >
                                            <TableCell>
                                                <Chip
                                                    label={page * rowsPerPage + index + 1}
                                                    color="primary"
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell sx={{ fontWeight: 'medium' }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                    <PackageIcon color="action" sx={{ mr: 1, fontSize: 16 }} />
                                                    {product.name}
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={product.category_name}
                                                    icon={<Category fontSize="small" />}
                                                    size="small"
                                                    color="secondary"
                                                    variant="outlined"
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={unitName}
                                                    icon={<Scale fontSize="small" />}
                                                    size="small"
                                                    color="info"
                                                    variant="outlined"
                                                />
                                            </TableCell>
                                            <TableCell align="center">
                                                <Chip
                                                    label={formatNumberWithComma(product.stock || 0)}
                                                    icon={<PackageIcon fontSize="small" />}
                                                    size="small"
                                                    sx={{
                                                        backgroundColor: product.stock > 10 ? 'success.light' :
                                                            product.stock > 0 ? 'warning.light' : 'error.light',
                                                        color: 'white'
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell align="center">
                                                <Chip
                                                    label={formatCurrency(product.cost || 0)}
                                                    color="success"
                                                    variant="outlined"
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell align="center">
                                                <Chip
                                                    label={formatCurrency(product.selling || 0)}
                                                    color="primary"
                                                    variant="outlined"
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell align="center">
                                                <Chip
                                                    label={formatCurrency(calculateProfit(product))}
                                                    color={calculateProfit(product) >= 0 ? 'success' : 'error'}
                                                    variant="outlined"
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell align="center">
                                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                    <CalendarMonth color="action" sx={{ mr: 1, fontSize: 16 }} />
                                                    {formatCreatedAtDate(product.updated_at)}
                                                </Box>
                                            </TableCell>
                                            <TableCell align="center">
                                                <Tooltip title="Actions">
                                                    <IconButton 
                                                        sx={{ color: theme.palette.action.main }}
                                                        onClick={(e) => handleActionMenuOpen(e, product)}
                                                        size="small"
                                                    >
                                                        <MoreVert />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                            
                            {/* Footer with Totals */}
                            {filteredData.length > 0 && (
                                <TableBody>
                                    <TableRow sx={{
                                        backgroundColor: theme.palette.primary.light + '20',
                                        borderTop: '3px solid',
                                        borderTopColor: theme.palette.primary.main,
                                        '& td': {
                                            fontWeight: 'bold',
                                            py: 2.5
                                        }
                                    }}>
                                        <TableCell colSpan={2} align="right">
                                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
                                                Totals ({filteredTotals.totalCount} products):
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', gap: 1 }}>
                                                <Chip
                                                    icon={<PackageIcon />}
                                                    label={`${filteredTotals.inStockItems} In Stock`}
                                                    size="small"
                                                    color="success"
                                                />
                                                <Chip
                                                    icon={<Warning />}
                                                    label={`${filteredTotals.outOfStockItems} Out`}
                                                    size="small"
                                                    color="error"
                                                />
                                            </Box>
                                        </TableCell>
                                        <TableCell></TableCell>
                                        <TableCell align="center">
                                            <Chip
                                                label={`Total: ${formatNumberWithComma(filteredTotals.totalStock)} pcs`}
                                                size="small"
                                                color="primary"
                                            />
                                        </TableCell>
                                        <TableCell align="center">
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                <Typography variant="body2" sx={{ color: theme.palette.success.main }}>
                                                    {formatCurrency(filteredTotals.totalCostValue)}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    Avg: {formatCurrency(filteredTotals.averageCostPrice)}
                                                </Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                <Typography variant="body2" sx={{ color: theme.palette.primary.main }}>
                                                    {formatCurrency(filteredTotals.totalSellingValue)}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    Avg: {formatCurrency(filteredTotals.averageSellingPrice)}
                                                </Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Chip
                                                label={formatCurrency(filteredTotals.totalProfitPotential)}
                                                color={filteredTotals.totalProfitPotential >= 0 ? 'success' : 'error'}
                                                size="small"
                                                icon={filteredTotals.totalProfitPotential >= 0 ? <TrendingUp /> : <TrendingDown />}
                                            />
                                        </TableCell>
                                        <TableCell colSpan={2}></TableCell>
                                    </TableRow>
                                </TableBody>
                            )}
                        </Table>
                    </TableContainer>

                    <TablePagination
                        rowsPerPageOptions={[5, 10, 25, 50, 100]}
                        component="div"
                        count={filteredData.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        sx={{ mt: 2 }}
                    />

                    {filteredData.length === 0 && (
                        <Box sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            py: 8,
                            color: 'text.secondary'
                        }}>
                            <PackageIcon sx={{ fontSize: 60, mb: 2 }} />
                            <Typography variant="h6">No products found</Typography>
                            <Typography variant="body2">Try adjusting your search query or filters</Typography>
                        </Box>
                    )}
                </Paper>
            </motion.div>

            {/* Filter Options Dialog */}
            <Dialog
                open={filterOptions}
                TransitionComponent={Transition}
                fullWidth
                maxWidth="sm"
                onClose={(event, reason) => {
                    if (reason !== 'backdropClick') {
                        handleCloseOptions();
                    }
                }}
            >
                <DialogTitle sx={{ textAlign: 'center' }}>Filter Inventory</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, p: 2 }}>
                        <Button
                            variant="outlined"
                            color="primary"
                            startIcon={<RefreshIcon />}
                            onClick={clearFilters}
                            sx={{ textTransform: 'none' }}
                        >
                            Show All Products
                        </Button>

                        <Button
                            variant="outlined"
                            color="primary"
                            startIcon={<TodayIcon />}
                            onClick={applyTodayFilter}
                            sx={{ textTransform: 'none' }}
                        >
                            Today's Updates
                        </Button>

                        <Button
                            variant="outlined"
                            color="primary"
                            startIcon={<CalendarMonth />}
                            onClick={applyThisMonthFilter}
                            sx={{ textTransform: 'none' }}
                        >
                            This Month's Updates
                        </Button>

                        <Divider>OR</Divider>

                        <TextField
                            fullWidth
                            label="Product Name"
                            name="name"
                            value={filters.name}
                            onChange={handleFilterChange}
                        />

                        <FormControl fullWidth>
                            <InputLabel>Category</InputLabel>
                            <Select
                                name="category"
                                value={filters.category}
                                onChange={handleFilterSelectChange}
                                label="Category"
                            >
                                <MenuItem value="">All Categories</MenuItem>
                                {pageState.categoriesData.map((category) => (
                                    <MenuItem key={category.id} value={category.id}>
                                        {category.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl fullWidth>
                            <InputLabel>Unit of Measure</InputLabel>
                            <Select
                                name="unit_id"
                                value={filters.unit_id}
                                onChange={handleFilterSelectChange}
                                label="Unit of Measure"
                            >
                                <MenuItem value="">All Units</MenuItem>
                                {pageState.unitsData.map((unit) => (
                                    <MenuItem key={unit.id} value={unit.id}>
                                        {unit.name} ({unit.code})
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <Divider>OR</Divider>

                        <Typography variant="subtitle1" sx={{ textAlign: 'center' }}>
                            Filter by Stock Range
                        </Typography>

                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Min Stock"
                                    name="minStock"
                                    type="number"
                                    value={filters.minStock}
                                    onChange={handleFilterChange}
                                    InputProps={{ inputProps: { min: 0 } }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Max Stock"
                                    name="maxStock"
                                    type="number"
                                    value={filters.maxStock}
                                    onChange={handleFilterChange}
                                    InputProps={{ inputProps: { min: 0 } }}
                                />
                            </Grid>
                        </Grid>

                        <Divider>OR</Divider>

                        <Typography variant="subtitle1" sx={{ textAlign: 'center' }}>
                            Filter by Date Range
                        </Typography>

                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="From Date"
                                    type="date"
                                    name="dateFrom"
                                    value={filters.dateFrom}
                                    onChange={handleFilterChange}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="To Date"
                                    type="date"
                                    name="dateTo"
                                    value={filters.dateTo}
                                    onChange={handleFilterChange}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                        </Grid>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button color="error" onClick={handleCloseOptions} variant="contained">
                        Close
                    </Button>
                    <Button onClick={applyFilters} color="primary" variant="contained">
                        Apply Filters
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Adjust Stock Dialog with Searchable Product Dropdown */}
            <Dialog
                open={openAdjustment}
                onClose={(event, reason) => {
                    if (reason !== 'backdropClick') {
                        handleCloseAdjustment();
                    }
                }}
                TransitionComponent={Transition}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle sx={{ textAlign: 'center' }}>
                    <b>Adjust Stock</b>
                </DialogTitle>
                <DialogContent>
                    <Box component="form" onSubmit={handleAdjustStock} sx={{ mt: 2 }}>
                        {selectedProduct && (
                            <Alert severity="info" sx={{ mb: 3 }}>
                                <Typography variant="body2">
                                    <strong>Product:</strong> {selectedProduct.name}
                                </Typography>
                                <Typography variant="body2">
                                    <strong>Current Stock:</strong> {formatNumberWithComma(selectedProduct.stock)} pcs
                                </Typography>
                                <Typography variant="body2">
                                    <strong>Unit:</strong> {getUnitName(selectedProduct)}
                                </Typography>
                            </Alert>
                        )}

                        <Autocomplete
                            id="product-select-adjustment"
                            options={pageState.productsData}
                            loading={loading}
                            value={pageState.productsData.find(p => p.id === inventoryForm.productId) || null}
                            onChange={handleProductSelect}
                            inputValue={productSearchInput}
                            onInputChange={(event, newInputValue) => {
                                setProductSearchInput(newInputValue);
                            }}
                            getOptionLabel={(option) => `${option.name} (Stock: ${option.stock} ${getUnitCode(option)})`}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            renderOption={(props, option) => (
                                <li {...props}>
                                    <Box>
                                        <Typography variant="body1">
                                            {option.name}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            Stock: {formatNumberWithComma(option.stock)} {getUnitCode(option)} | 
                                            Category: {option.category_name} | 
                                            Unit: {getUnitName(option)}
                                        </Typography>
                                    </Box>
                                </li>
                            )}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Search Product *"
                                    placeholder="Type product name to search..."
                                    required
                                    helperText="Search by product name, category, or unit"
                                    sx={{ mb: 2 }}
                                    InputProps={{
                                        ...params.InputProps,
                                        startAdornment: (
                                            <>
                                                <InputAdornment position="start">
                                                    <Search />
                                                </InputAdornment>
                                                {params.InputProps.startAdornment}
                                            </>
                                        ),
                                    }}
                                />
                            )}
                        />

                        <FormControl fullWidth sx={{ mb: 2 }}>
                            <InputLabel>Action Type</InputLabel>
                            <Select
                                name="actionType"
                                value={inventoryForm.actionType}
                                onChange={handleInventoryInputChange}
                                label="Action Type"
                            >
                                <MenuItem value="restock">
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <TrendingUp color="success" />
                                        <Typography>Add Stock (Restock)</Typography>
                                    </Box>
                                </MenuItem>
                                <MenuItem value="adjustment">
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <TrendingDown color="error" />
                                        <Typography>Remove Stock (Adjustment)</Typography>
                                    </Box>
                                </MenuItem>
                            </Select>
                        </FormControl>

                        <TextField
                            fullWidth
                            label={`Quantity to ${inventoryForm.actionType === 'restock' ? 'Add' : 'Remove'} (${selectedProduct ? getUnitCode(selectedProduct) : 'pcs'})`}
                            name="quantity"
                            type="number"
                            value={inventoryForm.quantity}
                            onChange={handleInventoryInputChange}
                            required
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <PackageIcon />
                                    </InputAdornment>
                                ),
                                inputProps: { 
                                    min: 1,
                                    max: inventoryForm.actionType === 'adjustment' ? (selectedProduct?.stock || 0) : undefined
                                }
                            }}
                            helperText={
                                inventoryForm.actionType === 'adjustment' && selectedProduct 
                                    ? `Maximum: ${selectedProduct.stock} ${getUnitCode(selectedProduct)}` 
                                    : `Enter a positive number (in ${selectedProduct ? getUnitCode(selectedProduct) : 'pcs'})`
                            }
                            sx={{ mb: 2 }}
                        />

                        <TextField
                            fullWidth
                            multiline
                            rows={3}
                            label="Reason"
                            name="reason"
                            value={inventoryForm.reason}
                            onChange={handleInventoryInputChange}
                            required
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Description />
                                    </InputAdornment>
                                ),
                            }}
                            sx={{ mb: 2 }}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseAdjustment} color="error" variant="contained">
                        Cancel
                    </Button>
                    <Button
                        onClick={handleAdjustStock}
                        variant="contained"
                        color={inventoryForm.actionType === 'restock' ? 'success' : 'error'}
                        startIcon={inventoryForm.actionType === 'restock' ? <AddIcon /> : <RemoveIcon />}
                    >
                        {inventoryForm.actionType === 'restock' ? 'Add Stock' : 'Remove Stock'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Inventory Management Modal with Searchable Product Dropdown */}
            <Dialog
                open={openInventoryModal}
                onClose={(event, reason) => {
                    if (reason !== 'backdropClick') {
                        handleCloseInventoryModal();
                    }
                }}
                TransitionComponent={Transition}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle sx={{ textAlign: 'center' }}>
                    <b>Inventory Management</b>
                </DialogTitle>
                <DialogContent>
                    <Box component="form" onSubmit={handleAdjustStock} sx={{ mt: 2 }}>
                        <FormControl fullWidth sx={{ mb: 2 }}>
                            <InputLabel>Action Type</InputLabel>
                            <Select
                                name="actionType"
                                value={inventoryForm.actionType}
                                onChange={handleInventoryInputChange}
                                label="Action Type"
                            >
                                <MenuItem value="restock">
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <TrendingUp color="success" />
                                        <Typography>Add Stock (Restock)</Typography>
                                    </Box>
                                </MenuItem>
                                <MenuItem value="adjustment">
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <TrendingDown color="error" />
                                        <Typography>Remove Stock (Adjustment)</Typography>
                                    </Box>
                                </MenuItem>
                            </Select>
                        </FormControl>

                        <Autocomplete
                            id="product-select-inventory"
                            options={pageState.productsData}
                            loading={loading}
                            value={pageState.productsData.find(p => p.id === inventoryForm.productId) || null}
                            onChange={handleProductSelect}
                            inputValue={productSearchInput}
                            onInputChange={(event, newInputValue) => {
                                setProductSearchInput(newInputValue);
                            }}
                            getOptionLabel={(option) => `${option.name} (Stock: ${option.stock} ${getUnitCode(option)})`}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            renderOption={(props, option) => (
                                <li {...props}>
                                    <Box>
                                        <Typography variant="body1">
                                            {option.name}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            Stock: {formatNumberWithComma(option.stock)} {getUnitCode(option)} | 
                                            Category: {option.category_name} | 
                                            Unit: {getUnitName(option)}
                                        </Typography>
                                    </Box>
                                </li>
                            )}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Search Product *"
                                    placeholder="Type product name to search..."
                                    required
                                    helperText="Search by product name, category, or unit"
                                    sx={{ mb: 2 }}
                                    InputProps={{
                                        ...params.InputProps,
                                        startAdornment: (
                                            <>
                                                <InputAdornment position="start">
                                                    <Search />
                                                </InputAdornment>
                                                {params.InputProps.startAdornment}
                                            </>
                                        ),
                                    }}
                                />
                            )}
                        />

                        <TextField
                            fullWidth
                            label={`Quantity (${selectedProduct ? getUnitCode(selectedProduct) : 'pcs'})`}
                            name="quantity"
                            type="number"
                            value={inventoryForm.quantity}
                            onChange={handleInventoryInputChange}
                            required
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <PackageIcon />
                                    </InputAdornment>
                                ),
                                inputProps: { 
                                    min: 1,
                                    max: inventoryForm.actionType === 'adjustment' ? (selectedProduct?.stock || 0) : undefined
                                }
                            }}
                            helperText={
                                inventoryForm.actionType === 'adjustment' && selectedProduct 
                                    ? `Maximum: ${selectedProduct.stock} ${getUnitCode(selectedProduct)}` 
                                    : `Enter a positive number (in ${selectedProduct ? getUnitCode(selectedProduct) : 'pcs'})`
                            }
                            sx={{ mb: 2 }}
                        />

                        <TextField
                            fullWidth
                            multiline
                            rows={3}
                            label="Reason (Optional)"
                            name="reason"
                            value={inventoryForm.reason}
                            onChange={handleInventoryInputChange}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Description />
                                    </InputAdornment>
                                ),
                            }}
                            sx={{ mb: 2 }}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseInventoryModal} color="error" variant="contained">
                        Cancel
                    </Button>
                    <Button
                        onClick={handleAdjustStock}
                        variant="contained"
                        color="primary"
                        startIcon={inventoryForm.actionType === 'restock' ? <AddIcon /> : <RemoveIcon />}
                    >
                        {inventoryForm.actionType === 'restock' ? 'Add Stock' : 'Remove Stock'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* History Dialog */}
            <Dialog
                open={openHistory}
                onClose={(event, reason) => {
                    if (reason !== 'backdropClick') {
                        handleCloseHistory();
                    }
                }}
                TransitionComponent={Transition}
                fullWidth
                maxWidth="lg"
            >
                <DialogTitle sx={{ textAlign: 'center' }}>
                    <b>Inventory History - {selectedProduct?.name}</b>
                    {selectedProduct && (
                        <Typography variant="body2" color="text.secondary">
                            Unit: {getUnitName(selectedProduct)} | Current Stock: {formatNumberWithComma(selectedProduct.stock)} {getUnitCode(selectedProduct)}
                        </Typography>
                    )}
                </DialogTitle>
                <DialogContent>
                    {historyLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                            <CircularProgress />
                        </Box>
                    ) : pageState.inventoryHistory.length > 0 ? (
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow sx={{
                                        backgroundColor: theme.palette.primary.main,
                                        '& th': {
                                            color: 'white',
                                            fontWeight: 'bold',
                                            fontSize: '0.95rem'
                                        }
                                    }}>
                                        <TableCell>Date & Time</TableCell>
                                        <TableCell>Action</TableCell>
                                        <TableCell align="right">Old Stock</TableCell>
                                        <TableCell align="right">Change</TableCell>
                                        <TableCell align="right">New Stock</TableCell>
                                        <TableCell>User</TableCell>
                                        <TableCell>Reason</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {pageState.inventoryHistory.map((history) => {
                                        const actionInfo = formatActionType(history.action_type);
                                        return (
                                            <TableRow key={history.id}>
                                                <TableCell>{formatCreatedAtDate(history.created_at)}</TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={actionInfo.label}
                                                        color={actionInfo.color}
                                                        size="small"
                                                        icon={actionInfo.icon}
                                                    />
                                                </TableCell>
                                                <TableCell align="right">
                                                    {formatNumberWithComma(history.old_quantity)} {getUnitCode(selectedProduct)}
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            color: history.action_type === 'restock' || history.action_type === 'return'
                                                                ? 'success.main'
                                                                : 'error.main',
                                                            fontWeight: 'bold'
                                                        }}
                                                    >
                                                        {history.action_type === 'restock' || history.action_type === 'return' ? '+' : '-'}
                                                        {formatNumberWithComma(history.quantity)} {getUnitCode(selectedProduct)}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell align="right">
                                                    {formatNumberWithComma(history.new_quantity)} {getUnitCode(selectedProduct)}
                                                </TableCell>
                                                <TableCell>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <AccountCircle fontSize="small" />
                                                        <Typography variant="body2">
                                                            {history.user_name || 'Unknown'}
                                                        </Typography>
                                                    </Box>
                                                </TableCell>
                                                <TableCell>
                                                    {history.reason || 'No reason provided'}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    ) : (
                        <Box sx={{ p: 4, textAlign: 'center' }}>
                            <PackageIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                            <Typography variant="h6" color="text.secondary">
                                No inventory history found
                            </Typography>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    {hasPermission('PRINT') && selectedProduct && pageState.inventoryHistory.length > 0 && (
                        <Button 
                            onClick={() => handlePrintHistory(selectedProduct)} 
                            color="info" 
                            variant="contained"
                            startIcon={<Print />}
                        >
                            Print History
                        </Button>
                    )}
                    <Button onClick={handleCloseHistory} color="error" variant="contained">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Container>
    );
};

export default Inventory;